import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export type ActivityType = 'call' | 'email' | 'meeting' | 'note' | 'task' | 'system' | 'ecomail_synced' | 'owner_verification';

export interface Activity {
  id: string;
  tenantId: string;
  userId: string;
  leadId?: string | null;
  contactId?: string | null;
  type: ActivityType;
  subject: string;
  body?: string | null;
  meta?: Record<string, any>;
  dueAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    fullName: string;
    avatarUrl?: string | null;
  };
}

export interface CreateActivityDto {
  leadId?: string;
  contactId?: string;
  type: ActivityType;
  subject: string;
  body?: string;
  dueAt?: string;
  meta?: Record<string, any>;
}

export interface UpdateActivityDto {
  subject?: string;
  body?: string;
  dueAt?: string;
  completedAt?: string;
  meta?: Record<string, any>;
}

interface ActivitiesFilters {
  leadId?: string;
  contactId?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

interface ActivitiesResponse {
  data: Activity[];
  total: number;
  limit: number;
  offset: number;
}

export function useActivities(filters: ActivitiesFilters = {}) {
  const { currentTenant } = useTenant();

  const params = new URLSearchParams();
  if (filters.leadId) params.set('leadId', filters.leadId);
  if (filters.contactId) params.set('contactId', filters.contactId);
  if (filters.type) params.set('type', filters.type);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));

  const queryString = params.toString();
  const path = `/activities${queryString ? `?${queryString}` : ''}`;

  const query = useQuery({
    queryKey: ['activities', filters, currentTenant?.id],
    queryFn: () => api.get<ActivitiesResponse>(path),
    enabled: !!currentTenant && !!(filters.leadId || filters.contactId),
  });

  return {
    ...query,
    data: query.data?.data,
    total: query.data?.total,
  };
}

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateActivityDto) => api.post<Activity>('/activities', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      if (variables.leadId) {
        queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
      if (variables.contactId) {
        queryClient.invalidateQueries({ queryKey: ['contact', variables.contactId] });
      }
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActivityDto }) =>
      api.put<Activity>(`/activities/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/activities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  call: 'Hovor',
  email: 'Email',
  meeting: 'Schůzka',
  note: 'Poznámka',
  task: 'Úkol',
  system: 'Systém',
  ecomail_synced: 'Odeslán do Ecomail',
  owner_verification: 'Ověření vlastníka',
};

export const ACTIVITY_TYPE_ICONS: Record<ActivityType, string> = {
  call: 'PhoneIcon',
  email: 'EnvelopeIcon',
  meeting: 'CalendarIcon',
  note: 'DocumentTextIcon',
  task: 'CheckCircleIcon',
  system: 'CogIcon',
  ecomail_synced: 'EnvelopeIcon',
  owner_verification: 'CheckBadgeIcon',
};
